import { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSIconButton from "views/system-design/DSIconButton";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import DSAvatar from "views/system-design/DSAvatar";
import DSTooltip from "views/system-design/DSTooltip";
import DSErrorButton from "views/system-design/DSErrorButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import PersonIcon from "@mui/icons-material/Person";
import LabelIcon from "@mui/icons-material/Label";
import { Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import { useTicketDetail } from "../../hooks/useTicketQueries";
import {
  getInitials,
  getAvatarColor,
  getAssignableUsers,
  findUserByAssignee,
} from "../../utils";
import useUsers from "components/hooks/useUsers";
import { Author } from "api/author";
import TicketComments from "../detail/TicketComments";
import TicketAttachments from "../detail/TicketAttachments";
import SectionHeader from "../shared/SectionHeader";
import LabelChips from "../shared/LabelChips";

interface KlydeaDetailModalProps {
  ticket: Ticket;
  onClose: () => void;
}

function EditableTitle({
  value,
  onSave,
}: {
  value: string;
  onSave: (title: string) => void;
}) {
  const [editing, setEditing] = useState<{ draft: string } | null>(null);

  const save = () => {
    if (!editing) return;
    if (editing.draft.trim() && editing.draft.trim() !== value) {
      onSave(editing.draft.trim());
    }
    setEditing(null);
  };

  if (editing) {
    return (
      <DSTextField
        value={editing.draft}
        onChange={(event) =>
          setEditing((prev) => (prev ? { draft: event.target.value } : prev))
        }
        fullWidth
        size="small"
        autoFocus
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") setEditing(null);
        }}
        onBlur={save}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": { fontSize: 20, fontWeight: 600 },
        }}
      />
    );
  }

  return (
    <DSTypography
      variant="h5"
      sx={{
        fontWeight: 600,
        mb: 2,
        cursor: "pointer",
        "&:hover": { color: "primary.main" },
      }}
      onClick={() => setEditing({ draft: value })}
    >
      {value}
    </DSTypography>
  );
}

function EditableDescription({
  value,
  onSave,
}: {
  value: string;
  onSave: (description: string) => void;
}) {
  const [editing, setEditing] = useState<{ draft: string } | null>(null);

  const save = () => {
    if (!editing) return;
    onSave(editing.draft);
    setEditing(null);
  };

  if (editing) {
    return (
      <DSBox>
        <DSTextField
          value={editing.draft}
          onChange={(event) =>
            setEditing((prev) => (prev ? { draft: event.target.value } : prev))
          }
          fullWidth
          multiline
          minRows={3}
          autoFocus
          InputProps={{
            sx: { maxHeight: 300, overflowY: "auto" },
          }}
        />
        <DSBox sx={{ display: "flex", gap: 1, mt: 1 }}>
          <DSIconButton size="small" onClick={save} color="primary">
            <CheckIcon fontSize="small" />
          </DSIconButton>
          <DSIconButton size="small" onClick={() => setEditing(null)}>
            <CloseIcon fontSize="small" />
          </DSIconButton>
        </DSBox>
      </DSBox>
    );
  }

  return (
    <DSBox
      onClick={() => setEditing({ draft: value })}
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: "white.5",
        cursor: "pointer",
        minHeight: 60,
        "&:hover": { bgcolor: "white.8" },
      }}
    >
      <DSTypography
        variant="body2"
        sx={{
          whiteSpace: "pre-wrap",
          color: value ? "text.primary" : "text.disabled",
        }}
      >
        {value || "Click to add a description..."}
      </DSTypography>
    </DSBox>
  );
}

export default function KlydeaDetailModal({
  ticket,
  onClose,
}: KlydeaDetailModalProps) {
  const theme = useTheme();
  const { updateTicket, deleteTicket } = useTickets();
  const { data: allUsers } = useUsers();
  const assigneeOptions = getAssignableUsers(allUsers ?? []);
  const current = useTicketDetail(ticket);

  const handleDelete = () => {
    deleteTicket(current.id);
    onClose();
  };

  const handleConvertToTicket = () => {
    updateTicket(current.id, { status: "todo" });
    onClose();
  };

  const handleToggleLabel = (label: string) => {
    const updatedLabels = current.labels.includes(label)
      ? current.labels.filter((existing) => existing !== label)
      : [...current.labels, label];
    updateTicket(current.id, { labels: updatedLabels });
  };

  return (
    <DSDialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DSBox
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.main} 100%)`,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LightbulbIcon
            sx={{ color: alpha(theme.palette.common.white, 0.9), fontSize: 22 }}
          />
          <DSTypography
            variant="subtitle1"
            sx={{ color: "common.white", fontWeight: 600 }}
          >
            {current.key || "Klydea"}
          </DSTypography>
        </DSBox>
        <DSIconButton
          size="small"
          onClick={onClose}
          sx={{ color: "grey.300", "&:hover": { color: "common.white" } }}
        >
          <CloseIcon fontSize="small" />
        </DSIconButton>
      </DSBox>

      <DSDialogContent sx={{ p: 0 }}>
        <DSBox sx={{ px: 3, pt: 3, pb: 1 }}>
          <EditableTitle
            value={current.title}
            onSave={(title) => updateTicket(current.id, { title })}
          />

          <DSTypography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 1 }}
          >
            Description
          </DSTypography>
          <EditableDescription
            value={current.description}
            onSave={(description) => updateTicket(current.id, { description })}
          />
        </DSBox>

        <DSBox sx={{ px: 3, py: 2, display: "flex", gap: 3 }}>
          <DSBox sx={{ flex: 1 }}>
            <SectionHeader
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              title="Reporter"
            />
            <DSAutocomplete<Author>
              options={assigneeOptions}
              getOptionLabel={(option) => option.name.trim()}
              value={
                assigneeOptions.find((user) => user.id === current.creatorId) ??
                null
              }
              onChange={(_event, selectedUser) =>
                updateTicket(current.id, {
                  creatorId: selectedUser?.id ?? "",
                })
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <DSBox
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <DSAvatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: getAvatarColor(option.name),
                      }}
                    >
                      {getInitials(option.name)}
                    </DSAvatar>
                    <DSTypography variant="body2">{option.name}</DSTypography>
                  </DSBox>
                </li>
              )}
              renderInput={(params) => (
                <DSTextField
                  {...params}
                  placeholder="No reporter"
                  fullWidth
                  size="small"
                />
              )}
            />
          </DSBox>
          <DSBox sx={{ flex: 1 }}>
            <SectionHeader
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              title="Assignee"
            />
            <DSAutocomplete<Author>
              options={assigneeOptions}
              getOptionLabel={(option) => option.name.trim()}
              value={findUserByAssignee(current.assignee, assigneeOptions)}
              onChange={(_event, selectedUser) =>
                updateTicket(current.id, {
                  assignee: selectedUser?.id ?? "",
                })
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <DSBox
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <DSAvatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: getAvatarColor(option.name),
                      }}
                    >
                      {getInitials(option.name)}
                    </DSAvatar>
                    <DSTypography variant="body2">{option.name}</DSTypography>
                  </DSBox>
                </li>
              )}
              renderInput={(params) => (
                <DSTextField
                  {...params}
                  placeholder="Unassigned"
                  fullWidth
                  size="small"
                />
              )}
            />
          </DSBox>
        </DSBox>

        <DSBox sx={{ px: 3, py: 2 }}>
          <SectionHeader
            icon={<LabelIcon sx={{ fontSize: 16 }} />}
            title="Labels"
          />
          <LabelChips
            selectedLabels={current.labels}
            onToggleLabel={handleToggleLabel}
          />
        </DSBox>

        <DSBox sx={{ px: 3 }}>
          <TicketAttachments ticket={current} />
        </DSBox>

        <DSBox sx={{ px: 3, pb: 2 }}>
          <TicketComments ticket={current} />
        </DSBox>
      </DSDialogContent>

      <DSBox
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "white.5",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <DSTooltip title="Convert this idea into a ticket">
          <DSPrimaryButton
            size="small"
            startIcon={<ConfirmationNumberIcon />}
            onClick={handleConvertToTicket}
          >
            Convert to Ticket
          </DSPrimaryButton>
        </DSTooltip>
        <DSTooltip title="Delete this klydea">
          <DSErrorButton
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </DSErrorButton>
        </DSTooltip>
      </DSBox>
    </DSDialog>
  );
}
